import { AmplienceImage, AmplienceLinkType } from './amplience';
import { LinkLevel1Pro } from './spreadshirtProHeader';

export enum HeaderTypes {
  SPREADSHIRT = 'spreadshirt',
  SPREADSHIRT_PRO = 'spreadshirt-pro',
}

export interface HierarchyMeta {
  /**
   * Present on freshly fetched content (FullHierarchyMeta). After the hierarchy
   * is assembled, trimHierarchyMeta (fetchHierarchy.ts) keeps schema only on the
   * two node types the client discriminates on (navigation-cyo-button,
   * navigation-level1-pro); it is dropped everywhere else to shrink the payload.
   */
  schema?: string;
  deliveryId: string;
  /**
   * Only present on freshly fetched Amplience content (FullHierarchyMeta).
   * Everything besides schema/deliveryId is stripped after the hierarchy is
   * assembled (see trimHierarchyMeta in fetchHierarchy.ts) to keep the
   * serialized header payload small.
   */
  name?: string;
  deliveryKey?: string;
  hierarchy?: {
    root: boolean;
    parentId: string;
    position: string;
  };
}

// The untrimmed metadata as delivered by Amplience. schema is always present
// here (only the assembled/trimmed hierarchy may drop it, see trimHierarchyMeta).
export type FullHierarchyMeta = HierarchyMeta & {
  schema: string;
  name: string;
  hierarchy: {
    root: boolean;
    parentId: string;
    position: string;
  };
};

// Marks a descendant as carrying untrimmed metadata. Distributes over unions.
type WithFullHierarchyMeta<T> = T extends { content: infer C }
  ? C extends { _meta: HierarchyMeta }
    ? Omit<T, 'content'> & { content: Omit<C, '_meta'> & { _meta: FullHierarchyMeta } }
    : T
  : T;

export type LinkWithSubLabel = AmplienceLinkType & {
  subLabel: string;
};

export interface HeaderTile {
  link: AmplienceLinkType;
  image: AmplienceImage;
  subheadline?: string;
}

export interface HeaderTeaser {
  _meta: HierarchyMeta;
  headline: string;
  tiles: HeaderTile[];
}

export interface HeaderTeaserSlot {
  id: string;
  contentType: string;
  _meta: {
    schema: string;
  };
}

export interface MutatedHeaderTeaserSlot {
  _meta: {
    schema: string;
  };
  headerTeaser: HeaderTeaser;
}

export interface NavigationLink {
  content: {
    _meta: HierarchyMeta;
    link: AmplienceLinkType;
  };
}

export interface LinkLevel3 {
  content: {
    _meta: HierarchyMeta;
    column: NavigationLink[];
  };
}

export interface CYOItem {
  image: AmplienceImage;
  link: LinkWithSubLabel;
}

export interface Level2CyoItem {
  content: {
    _meta: HierarchyMeta;
    items: CYOItem[];
    link: AmplienceLinkType;
    headline: string;
    headerType: HeaderTypes.SPREADSHIRT;
  };
}

interface DisabledLevel2Link {
  isLinkEnabled: false;
  label: string;
  url: never;
  openInNewTab: never;
  icon: never;
  tracking: never;
}

interface EnabledLevel2Link extends AmplienceLinkType {
  isLinkEnabled: true;
}

export type Level2Link = DisabledLevel2Link | EnabledLevel2Link;

export interface Level2LinkItem {
  content: {
    _meta: HierarchyMeta;
    link: Level2Link;
    headerTeaserSlot?: MutatedHeaderTeaserSlot;
    level3: LinkLevel3[];
    headerType: HeaderTypes.SPREADSHIRT;
  };
}

export type LinkLevel2 = Level2CyoItem | Level2LinkItem;

export interface LinkLevel1 {
  content: {
    _meta: HierarchyMeta;
    link: Omit<LinkWithSubLabel, 'url'> & { url?: string };
    headerTeaserSlot?: MutatedHeaderTeaserSlot;
    level2: LinkLevel2[];
  };
}

export interface LinkLevel {
  content: {
    _meta: HierarchyMeta;
    link: AmplienceLinkType;
  };
}

export type DisabledLink = LinkWithSubLabel & {
  isLinkEnabled: false;
  label: never;
  url: never;
  openInNewTab: never;
};

export type EnabledLink = LinkWithSubLabel & {
  isLinkEnabled: true;
};

export interface MenuSocials {
  headline: string;
  iconLinks: { link: AmplienceLinkType }[];
}

export interface HeaderContent {
  content: {
    _meta: HierarchyMeta;
    headerType: HeaderTypes;
    level1: LinkLevel1[];
    b2bLink: EnabledLink | DisabledLink;
    startSellingLink: EnabledLink | DisabledLink;
    headerTeaserSlot?: MutatedHeaderTeaserSlot;
    menuFooter: { link: AmplienceLinkType }[];
    menuSocials: MenuSocials;
  };
}

// Types for fetching and populating headerTeaserSlots
type PreLinkLevel1 = Omit<LinkLevel1, 'content'> & {
  content: Omit<LinkLevel1['content'], 'headerTeaserSlot'> & {
    headerTeaserSlot?: HeaderTeaserSlot;
  };
};
type PreLinkLevel1Pro = Omit<LinkLevel1Pro, 'content'> & {
  content: Omit<LinkLevel1Pro['content'], 'headerTeaserSlot'> & {
    headerTeaserSlot?: HeaderTeaserSlot;
  };
};
type PreLinkLevel2 = Omit<LinkLevel2, 'content'> & {
  content: Omit<LinkLevel2['content'], 'headerTeaserSlot'> & {
    headerTeaserSlot?: HeaderTeaserSlot;
  };
};
type PreLinkLevel3 = Omit<LinkLevel3, 'content'> & {
  content: Omit<LinkLevel3['content'], 'headerTeaserSlot'> & {
    headerTeaserSlot?: HeaderTeaserSlot;
  };
};

export type FetchedDescendant = WithFullHierarchyMeta<
  PreLinkLevel1 | PreLinkLevel1Pro | PreLinkLevel2 | PreLinkLevel3 | NavigationLink
>;
export type MutatedDescendant = WithFullHierarchyMeta<
  LinkLevel1 | LinkLevel1Pro | LinkLevel2 | LinkLevel3 | NavigationLink
>;

export type FetchedDescendants = {
  responses: FetchedDescendant[];
  page: {
    responseCount: number;
    cursor?: string;
  };
};

export type SlotToFetch = {
  index: number;
  id: string;
  descendant: FetchedDescendant;
}[];
